import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@osmix/ui/button";
import { api, type Product } from "@/api";
import { categoryKeys, useCategories } from "@/api/hooks/use-categories";
import { productKeys } from "@/api/hooks/use-products";
import { Select } from "@/components/ui/select";
import { buildCategoryTree, leafTargets } from "@/lib/category-tree";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";

/**
 * Карточка лежит в архивной категории (сам узел или его предок в архиве):
 * покупатель её в каталоге не найдёт, продавец не увидит узел в выборе.
 * Предупреждаем и даём перенести товар в действующий конечный узел, не
 * уходя со страницы.
 */
export function ArchivedCategoryNotice({
  product
}: {
  product: Product;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canMove = hasAdminPermission(user, "action.products.moderate");
  const {
    data
  } = useCategories();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moved, setMoved] = useState<string | null>(null);
  if (!data || !product.categoryId) return null;
  const tree = buildCategoryTree(data);
  const row = tree.byId.get(product.categoryId);
  if (moved) return <div className="rounded-md border border-emerald-600/40 bg-emerald-600/10 p-3 text-sm" data-testid="archived-category-moved">
				{t("products.archivedCategory.moved", {
      path: moved
    })}
			</div>;
  if (!row?.archived) return null;
  const targets = leafTargets(tree);
  const move = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await api.products.moveCategory(product.id, target);
      setMoved(tree.byId.get(target)?.path.join(" / ") ?? target);
      await Promise.all([queryClient.invalidateQueries({
        queryKey: productKeys.all
      }), queryClient.invalidateQueries({
        queryKey: categoryKeys.all
      })]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <div className="space-y-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-4 text-sm" data-testid="archived-category-notice">
			<div className="flex items-start gap-2">
				<AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
				<div className="space-y-1">
					<p className="font-medium">{t("products.archivedCategory.title")}</p>
					<p>{t("products.archivedCategory.body", {
            path: row.path.join(" / ")
          })}</p>
				</div>
			</div>
			{canMove && <div className="flex flex-wrap items-center gap-2 pl-6">
					<Select value={target} onChange={e_0 => setTarget(e_0.target.value)} aria-label={t("products.archivedCategory.target")} className="w-full sm:w-80" data-testid="archived-category-target">
						<option value="">{t("products.archivedCategory.choose")}</option>
						{targets.map(r => <option key={r.category.id} value={r.category.id}>
								{r.path.join(" / ")}
							</option>)}
					</Select>
					<Button size="sm" onClick={() => void move()} disabled={busy || !target} data-testid="archived-category-move">
						{t("products.archivedCategory.move")}
					</Button>
				</div>}
			{error && <p className="pl-6 text-destructive">{error}</p>}
		</div>;
}